import { sortBy } from 'lodash';

import { API_NAME, REDUX_STORE } from '@funnels/constants';
import { ApplicantActionTypes, ApplicantsReducerState, SharedActionTypes, StoreApplicant } from '@funnels/types';

function getSortByCustomerIdComparator(currentCustomerId?: string) {
  return (applicant: any) => {
    if (applicant.customerId === currentCustomerId) {
      return -1;
    }
    return 1;
  };
}

function sortByCreatedAt(a1: any) {
  return new Date(a1.createdAt).getTime();
}

const initialState: ApplicantsReducerState = {
  data: [],
  error: null,
  loading: 0,
};

export default function applicantsReducer(
  state: ApplicantsReducerState = initialState,
  action: ApplicantActionTypes | SharedActionTypes
): ApplicantsReducerState {
  switch (action.type) {
    case REDUX_STORE.SHARED_ACTION_TYPES.CLEAR_DATA:
      return {
        ...state,
        data: [],
      };
    case REDUX_STORE.SHARED_ACTION_TYPES.CLEAR_ERROR:
    case REDUX_STORE.APPLICANTS.ACTION_TYPES.CLEAR_APPLICANT_ERROR:
      return {
        ...state,
        error: null,
      };
    case REDUX_STORE.APPLICANTS.ACTION_TYPES.CREATE_APPLICANT:
      return {
        ...state,
        data: sortBy(
          [...state.data, action.applicant],
          [getSortByCustomerIdComparator(action.currentCustomerId), sortByCreatedAt]
        ),
        loading: state.loading + 1,
      };
    case REDUX_STORE.APPLICANTS.ACTION_TYPES.CREATE_APPLICANT_SUCCESS:
      return {
        ...state,
        data: sortBy(
          state.data.map((applicant) =>
            applicant.clientId === action.clientId
              ? { ...applicant, id: action.id, dependants: action.dependants, customerId: action.customerId }
              : applicant
          ),
          [getSortByCustomerIdComparator(action.currentCustomerId), sortByCreatedAt]
        ),
        loading: state.loading - 1,
      };
    case REDUX_STORE.APPLICANTS.ACTION_TYPES.DELETE_APPLICANT:
      return {
        ...state,
        data: state.data.filter((applicant) => applicant.clientId !== action.clientId),
        loading: state.loading + 1,
      };
    case REDUX_STORE.APPLICANTS.ACTION_TYPES.FETCH_APPLICANTS:
      return {
        ...state,
        data: [],
        loading: state.loading + 1,
      };
    case REDUX_STORE.APPLICANTS.ACTION_TYPES.FETCH_APPLICANTS_SUCCESS:
      const shouldUpdate = !state.version || state.version <= (action.version || 2);
      // in the case where the userId whitelist is used in LD, the flag value flips quickly (false => true) when the page first loads
      // that causes two fetch requests sent, both v1 and v2
      // having version number here avoids v1 response overriding v2 if v1 comes back later than v2.
      if (shouldUpdate) {
        return {
          ...state,
          data: sortBy(
            action.applicants.map((applicant) => ({ ...applicant, clientId: applicant.id })),
            [getSortByCustomerIdComparator(action.currentCustomerId), sortByCreatedAt]
          ),
          loading: state.loading - 1,
          version: action.version,
        };
      } else {
        return {
          ...state,
          loading: state.loading - 1,
        };
      }
    case REDUX_STORE.APPLICANTS.ACTION_TYPES.SET_APPLICANT_ERROR:
      // TODO: FUNNEL-1606 - use same pattern in employment reducer, setting error message in same action as fail/abort
      return {
        ...state,
        error: {
          name: API_NAME.APPLICANTS,
          message: action.message,
        },
      };
    case REDUX_STORE.APPLICANTS.ACTION_TYPES.UPDATE_APPLICANT:
      return {
        ...state,
        data: sortBy(
          state.data.map((applicant) =>
            applicant.clientId === action.applicant.clientId ? action.applicant : applicant
          ),
          [getSortByCustomerIdComparator(action.currentCustomerId), sortByCreatedAt]
        ),
        loading: state.loading + 1,
      };
    case REDUX_STORE.APPLICANTS.ACTION_TYPES.UPDATE_APPLICANT_INVITATION_STATUS:
      const { invitationStatus, applicantId } = action;

      return {
        ...state,
        data: state.data.map((applicant: StoreApplicant) =>
          applicant.id === applicantId ? { ...applicant, invitationStatus } : applicant
        ),
      };
    case REDUX_STORE.APPLICANTS.ACTION_TYPES.UPDATE_APPLICANT_SUCCESS:
      return {
        ...state,
        data: sortBy(
          state.data.map((applicant) =>
            applicant.id === action.id ? { ...applicant, dependants: action.dependants } : applicant
          ),
          [getSortByCustomerIdComparator(action.currentCustomerId), sortByCreatedAt]
        ),
        loading: state.loading - 1,
      };
    case REDUX_STORE.APPLICANTS.ACTION_TYPES.CREATE_APPLICANT_FAIL:
    case REDUX_STORE.APPLICANTS.ACTION_TYPES.DELETE_APPLICANT_FAIL:
    case REDUX_STORE.APPLICANTS.ACTION_TYPES.DELETE_APPLICANT_SUCCESS:
    case REDUX_STORE.APPLICANTS.ACTION_TYPES.FETCH_APPLICANTS_FAIL:
    case REDUX_STORE.APPLICANTS.ACTION_TYPES.UPDATE_APPLICANT_ABORT:
    case REDUX_STORE.APPLICANTS.ACTION_TYPES.UPDATE_APPLICANT_FAIL:
      return {
        ...state,
        loading: state.loading - 1,
      };
    default:
      return state;
  }
}
